import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RUOLI, IMG } from "@/lib/constants";
import { FlussoGara } from "@/components/FlussoGara";
import { FooterCEG } from "@/components/FooterCEG";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "project_manager" });
  const [error, setError] = useState(useLocation().state?.error || "");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const invito = new URLSearchParams(window.location.search).get("invito");
  const [invitoInfo, setInvitoInfo] = useState(null);
  useEffect(() => { if (invito) api.get(`/public/inviti/${invito}`).then((r) => { setInvitoInfo(r.data); setMode("register"); setForm((f) => ({ ...f, email: r.data.email, name: r.data.name, role: r.data.role })); }).catch(() => setError("Invito non valido o già utilizzato")); }, [invito]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : { ...form, invito: invitoInfo ? invito : undefined };
      const { data } = await api.post(path, body);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white">
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative">
        <img src={IMG.growth} alt="Imprese e professionisti al lavoro" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/95 to-slate-100/90" />
        <div className="relative h-full flex flex-col justify-between p-14 text-slate-900">
          <div />
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-6 rounded-2xl bg-white px-6 py-4 shadow-xl mb-6" data-testid="login-logo-box">
              <img src="/brand/beaichy-logo.svg" alt="BeAIchy" className="h-20 sm:h-24" data-testid="login-logo" />
              <div className="h-16 w-px bg-slate-200" />
              <div className="flex flex-col items-center gap-1">
                <a href="https://www.casaenergiagreen.net" target="_blank" rel="noreferrer" title="Casa Energia Green" className="inline-flex hover:opacity-80"><img src="/brand/casa-energia-green.svg" alt="Casa Energia Green" className="h-12" data-testid="login-partner-logo" /></a>
                <a href="https://faberpro.app" target="_blank" rel="noreferrer" title="FaberPro" className="inline-flex hover:opacity-80"><img src="/brand/faberpro-logo.png" alt="FaberPro" className="h-5 w-auto" data-testid="login-faberpro-logo" /></a>
              </div>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight" data-testid="login-slogan">L'incentivo giusto per la tua impresa.<br />Alla pratica ci pensa BeAIchy.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed text-base">Fondo perduto, crediti d'imposta, finanziamenti agevolati, bandi e gare pubbliche per ogni codice ATECO: il team di legali, commercialisti, ingegneri e marketing verifica i requisiti, prepara la domanda e segue la pratica a step fino all'incasso. Paghi solo il lavoro svolto.</p>
            <p className="mt-2 text-slate-600 text-sm" data-testid="login-progetto-ceg"><strong>BeAIchy è un progetto di Casa Energia Green</strong>, condiviso con <strong>FaberPro.app</strong>: lo strumento per imprese e professionisti che vogliono partecipare ai bandi con una squadra completa.</p>
            <div className="mt-6"><FlussoGara /></div>
          </div>
          <p className="text-xs text-slate-600">Studio preliminare € 500 · Accordo a step · Success fee solo a esito positivo · Rinuncia libera dopo ogni step</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-14">
        <div className="w-full max-w-md fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{mode === "login" ? "Area riservata BeAIchy" : "Nuovo membro del team"}</p>
          {invitoInfo && <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900" data-testid="invito-banner">Benvenuto {invitoInfo.name}: sei stato invitato come <strong>{RUOLI[invitoInfo.role]?.label}</strong>{invitoInfo.titolo && ` (${invitoInfo.titolo})`}. Scegli una password per attivare l'account.</div>}
          <img src="/brand/beaichy-logo.svg" alt="BeAIchy" className="h-12 mt-3 lg:hidden" />
          <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-2">{mode === "login" ? "Accedi" : "Crea il tuo account"}</h1>
          <p className="text-sm text-slate-500 mt-2">{mode === "login" ? "Inserisci le credenziali del team BeAIchy." : "Scegli il tuo ruolo professionale: vedrai i task di tua competenza."}</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Nome e cognome</Label>
                <Input id="name" required value={form.name} onChange={set("name")} className="bg-slate-50 h-11" data-testid="register-name-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} readOnly={!!invitoInfo} className="bg-slate-50 h-11" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="bg-slate-50 h-11" data-testid="login-password-input" />
            </div>
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="role">Ruolo</Label>
                <select id="role" value={form.role} onChange={set("role")} data-testid="register-role-select"
                  className="w-full h-11 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#003087]">
                  {Object.entries(RUOLI).filter(([k]) => k !== "admin").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            )}
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#003087] hover:bg-[#0070BA] rounded-md shadow-sm hover:shadow-md transition-colors" data-testid="auth-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {mode === "login" ? "Accedi" : "Registrati"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" /> oppure <div className="h-px flex-1 bg-slate-200" /></div>
          <Button type="button" variant="outline" onClick={googleLogin} className="w-full h-11 rounded-md border-slate-300" data-testid="google-login-button">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continua con Google
          </Button>

          <p className="text-sm text-slate-500 mt-8 text-center">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-[#003087] font-medium hover:underline" data-testid="toggle-auth-mode">
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </div>
      </div>
    </div>
    <FooterCEG compact />
    </div>
  );
}
